/**
 * THE OUTREACH TABS CARRY THEIR COUNTS (§AL, the approved Outreach mockup: *To approve 127 · Sent 144
 * · Accepted 2 · Declined 1*). Pitch status became tabs in round 309; a tab that says how many rows it
 * holds is the one place a reader learns there are 127 follow-ups waiting without opening the page.
 *
 * ⚠ **Painted into a span BESIDE the label, never into it.** `applyStaticI18n` rewrites the text of
 * every `[data-i18n]` node on a locale change, which is why the attention badge is a sibling of its
 * link - a count written into the label would vanish on the first language switch.
 * ⚠ **A zero is not drawn** (rule 03: a number only where it says something). "Accepted 0" on every
 * visit is a scoreboard for a game nobody has started; an empty tab says so when you open it.
 * ⚠ **Counted by the server** (`GET /api/outreach/stats`), in the same terms each page lists: To approve
 * is the QUEUED pitches only - the number the page's own heading says - since a failed send is its own
 * row on Today's "Needs you" and a tab reading 4 over a heading reading 3 is two answers to one question.
 * Accepted and Rejected are rows of their own tables. Fetched only while the Outreach tabs are on screen.
 */
import { api } from "./api.ts";
import { formatNumber, localeStore } from "./i18n.ts";
import { sectionOf } from "../../shared/appPages.ts";

interface OutreachStats {
	queued?: number | null;
	approved?: number | null;
	sent?: number | null;
	accepted?: number | null;
	rejected?: number | null;
}

let last: Record<string, number> = {};
let inFlight = false;

function paint(counts: Record<string, number>): void {
	last = counts;
	document.querySelectorAll<HTMLAnchorElement>('.section-tabs[data-section="outreach"] a[data-page]').forEach(link => {
		const count = counts[link.dataset.page ?? ""] ?? 0;
		let span = link.querySelector<HTMLElement>(".tab-count");
		if (count <= 0) {
			span?.remove();
			return;
		}
		if (span == null) {
			span = document.createElement("span");
			span.className = "tab-count";
			link.append(span);
		}
		span.textContent = formatNumber(count);
	});
}

/** Count again - after a navigation into Outreach, or after a page there changed what it holds */
export async function refreshTabCounts(): Promise<void> {
	if (inFlight) return;
	inFlight = true;
	try {
		const stats = await api<OutreachStats>("/api/outreach/stats");
		paint({
			queue: stats.queued ?? 0,
			approved: stats.approved ?? 0,
			sent: stats.sent ?? 0,
			accepted: stats.accepted ?? 0,
			rejected: stats.rejected ?? 0
		});
	} catch {
		// The tabs keep what they last said; a count is chrome, and the pages stand without it.
	} finally {
		inFlight = false;
	}
}

/** The page on screen, read off the shell - the router marks it before its listeners run */
function onOutreach(): boolean {
	const page = document.querySelector(".page.active")?.id.replace(/^page-/, "") ?? "";
	return page !== "" && sectionOf(page) === "outreach";
}

/** Wire it up: count on entering Outreach, and repaint the numbers in the new locale's digits */
export function initTabCounts(onNavigate: (listener: () => void) => void): void {
	localeStore.subscribe(() => paint(last));
	onNavigate(() => {
		if (onOutreach()) void refreshTabCounts();
	});
	if (onOutreach()) void refreshTabCounts();
}
